import React, {useState, useEffect, useContext} from 'react';
import {
  Box,
  Toolbar,
  Typography,
  Container,
  Paper,
  Grid,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Button,
  IconButton,
  Stack,
  Divider,
} from '@mui/material';
import Title from '../../components/Common/Title';
import {HeadsetRounded} from '@mui/icons-material';
import {Context as AuthContext} from '../../contexts/AuthContext';
import {Context as FinancialContext} from '../../contexts/FinancialContext';

const headerStyle = {
  color: '#9d9db0',
  fontSize: 18,
  fontWeight: 700,
  letterSpacing: '1.6px',
  lineHeight: '24px',
  textTransform: 'uppercase',
  alignSelf: 'flex-start',
};

const paperStyle = {
  p: 2,
  display: 'flex',
  flexDirection: 'column',
  padding: '32px 32px',
  borderRadius: '12px',
};

const Profile = ({}) => {
  const {
    state: {uid, client, financial},
  } = useContext(AuthContext);
  const {
    state: {accounts, transactions},
  } = useContext(FinancialContext);

  return (
    <Box
      component="main"
      sx={{
        backgroundColor: '#fff',
        flexGrow: 1,
        height: '100vh',
        overflow: 'auto',
        padding: '60px',
      }}>
      <Container maxWidth="lg" sx={{mt: 4, mb: 4}}>
        <Paper sx={paperStyle} elevation={24}>
          <Typography
            component="h3"
            variant="h6"
            color="primary"
            gutterBottom
            style={headerStyle}>
            Month Summary
          </Typography>
          <Stack direction={'row'} spacing={4} justifyContent="space-between">
            <div>
              <Typography>Net Worth</Typography>
              <Typography style={{fontWeight: 'bold', textAlign: 'center'}}>
                {'$30,000'}
              </Typography>
            </div>
            <div>
              <Typography>Accounts</Typography>
              <Typography style={{fontWeight: 'bold', textAlign: 'center'}}>
                {accounts.length}
              </Typography>
            </div>

            <div>
              <Typography>Transactions</Typography>
              <Typography style={{fontWeight: 'bold', textAlign: 'center'}}>
                {transactions.length}
              </Typography>
            </div>
            <div>
              <Typography>Streak:</Typography>
              <Typography style={{fontWeight: 'bold', textAlign: 'center'}}>
                {financial ? financial.streak : 0}
              </Typography>
            </div>
          </Stack>
        </Paper>
        <Paper sx={paperStyle} elevation={24}>
          <Typography
            component="h3"
            variant="h6"
            color="primary"
            gutterBottom
            style={headerStyle}>
            Accounts
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell />
                <TableCell sx={{fontWeight: 'bold'}}>CREDIT </TableCell>
                <TableCell sx={{fontWeight: 'bold'}}>DEBIT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {accounts
                .sort((a, b) => {
                  const balA = findBalanceByAccount(a);
                  const balB = findBalanceByAccount(b);
                  return balB - balA;
                })
                .map(account => {
                  const name = account.official_name
                    ? account.official_name
                    : account.name;
                  const amount = findBalanceByAccount(account);
                  const prettyAmount = `$${amount.toFixed(2)}`;
                  return (
                    <TableRow key={account.account_id}>
                      <TableCell>
                        <Typography style={{fontWeight: 'bold'}}>
                          {name}
                        </Typography>
                      </TableCell>
                      <TableCell>{account.type}</TableCell>
                      <TableCell>
                        {account.type === 'credit' || account.type === 'debit'
                          ? prettyAmount
                          : null}
                      </TableCell>
                      <TableCell>
                        {account.type !== 'credit' && account.type !== 'debit'
                          ? prettyAmount
                          : null}
                      </TableCell>
                    </TableRow>
                  );
                })}
              <TableRow key={-1}>
                <TableCell />
                <TableCell />
                <TableCell>{'Sum Out'}</TableCell>
                <TableCell>{'SUM IN'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
        <Paper sx={paperStyle} elevation={24}>
          <Typography
            component="h3"
            variant="h6"
            color="primary"
            gutterBottom
            style={headerStyle}>
            Transactions
          </Typography>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell />
                <TableCell />
                <TableCell sx={{fontWeight: 'bold'}}>IN </TableCell>
                <TableCell sx={{fontWeight: 'bold'}}>OUT</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {transactions.map(trans => {
                const amount = `${trans.amount.toFixed(2)}`;
                return (
                  <TableRow key={trans.transaction_id}>
                    <TableCell>
                      <Typography style={{fontWeight: 'bold'}}>
                        {trans.name}
                      </Typography>
                    </TableCell>
                    <TableCell>{trans.date}</TableCell>
                    <TableCell>{trans.amount < 0 ? amount : null}</TableCell>
                    <TableCell>{trans.amount >= 0 ? amount : null}</TableCell>
                  </TableRow>
                );
              })}
              <TableRow key={-1}>
                <TableCell />
                <TableCell />
                <TableCell>{'Sum in'}</TableCell>
                <TableCell>{'SUM OUT'}</TableCell>
              </TableRow>
            </TableBody>
          </Table>
        </Paper>
      </Container>
    </Box>
  );
};

const findBalanceByAccount = account => {
  const {balances, accountType} = account;
  switch (accountType) {
    case 'credit':
    case 'debt':
      return -1 * balances.current;
    case 'investment':
      if (
        balances.current !== null &&
        balances.available !== null &&
        balances.current.toFixed(2) !== balances.available.toFixed(2)
      ) {
        return balances.current + balances.available;
      } else if (balances.available !== null) {
        return balances.available;
      } else {
        return balances.current;
      }
    default:
      return balances.available !== null
        ? balances.available
        : balances.current;
  }
};

const Transaction = ({item}) => {
  return (
    <Stack direction={'row'} alignItems="center" spacing={2}>
      <Typography style={{flex: 1}}>{item.name}</Typography>
      <Typography style={{flex: 1}}>
        {item.pending ? 'Pending' : 'Posted'}
      </Typography>
      <Typography style={{fontWeight: 'bold'}}>
        ${item.amount.toFixed(2)}
      </Typography>
    </Stack>
  );
};

const Account = ({item}) => {
  const name = item.official_name ? item.official_name : item.name;
  return (
    <Stack direction={'row'} alignItems="center" spacing={2}>
      <Typography style={{fontWeight: 'bold'}}>{name}</Typography>
      <Typography style={{flex: 1}}>{item.type}</Typography>
      <Typography style={{fontWeight: 'bold'}}>
        ${findBalanceByAccount(item).toFixed(2)}
      </Typography>
    </Stack>
  );
};

export default Profile;
